import React, { useEffect, useRef, useState } from "react";
import sfx from "@/game/sfx";
import { useTr } from "@/autoTranslate";

/**
 * Milkoş's Mice — mice pop up in a grid. Click as many as you can in 15s.
 * Each mouse stays 1200ms then vanishes. Miss too many = lower score.
 */
const STAGE_W = 640;
const STAGE_H = 340;
const COLS = 5;
const ROWS = 3;
const DURATION = 15; // seconds

export default function MilosMice({ onExit }) {
  const tr = useTr();
  const [best, setBest] = useState(() =>
    parseInt(localStorage.getItem("milos_mice_best") || "0", 10),
  );
  const [score, setScore] = useState(0);
  const [state, setState] = useState("idle");
  const [time, setTime] = useState(DURATION);
  const [mice, setMice] = useState([]); // array of {cell, id, tSpawn}
  const timerRef = useRef();
  const spawnRef = useRef();
  const scoreRef = useRef(0);
  const cellW = STAGE_W / COLS;
  const cellH = STAGE_H / ROWS;

  const start = () => {
    setScore(0);
    scoreRef.current = 0;
    setTime(DURATION);
    setMice([]);
    setState("playing");
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Escape") onExit && onExit();
      if ((e.code === "Space" || e.code === "Enter") && state !== "playing") {
        start();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onExit]);

  useEffect(() => {
    if (state !== "playing") return;
    const startTs = performance.now();
    timerRef.current = setInterval(() => {
      const el = (performance.now() - startTs) / 1000;
      const rem = Math.max(0, DURATION - el);
      setTime(rem);
      if (rem <= 0) {
        clearInterval(timerRef.current);
        clearInterval(spawnRef.current);
        setState("over");
        if (scoreRef.current >= 15) sfx.victory();
        else sfx.sad();
        if (scoreRef.current > best) {
          setBest(scoreRef.current);
          localStorage.setItem("milos_mice_best", String(scoreRef.current));
        }
      }
    }, 100);
    spawnRef.current = setInterval(() => {
      setMice((prev) => {
        const now = performance.now();
        const filtered = prev.filter((m) => now - m.tSpawn < 1200);
        // pick a random cell not currently occupied
        const occupied = new Set(filtered.map((m) => m.cell));
        const available = [];
        for (let i = 0; i < COLS * ROWS; i++) if (!occupied.has(i)) available.push(i);
        if (available.length === 0) return filtered;
        const cell = available[Math.floor(Math.random() * available.length)];
        return [...filtered, { cell, id: Date.now(), tSpawn: now }];
      });
    }, 480);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(spawnRef.current);
    };
  }, [state, best]);

  const hitMouse = (m) => {
    scoreRef.current += 1;
    setScore(scoreRef.current);
    sfx.pop();
    setMice((prev) => prev.filter((x) => x.id !== m.id));
  };

  return (
    <div className="w-full" data-testid="minigame-milos-mice" data-minigame-active="true">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Kedi oyunu")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr("Milkoş'un Fareleri")}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Rekor")} · {best}
          </div>
          <div className="display-serif italic text-3xl">{score}</div>
          <div className="font-mono-label text-[10px] opacity-70">
            {time.toFixed(1)}s
          </div>
        </div>
      </div>

      <div
        className="mt-6 relative mx-auto rounded-md border-2 border-[color:var(--ink)] overflow-hidden"
        style={{
          width: STAGE_W,
          maxWidth: "100%",
          height: STAGE_H,
          background:
            "repeating-linear-gradient(45deg, var(--blush) 0 24px, var(--pink) 24px 48px)",
        }}
      >
        {/* Grid lines (subtle) */}
        {Array.from({ length: COLS - 1 }).map((_, i) => (
          <div
            key={`c${i}`}
            className="absolute top-0 bottom-0 border-l border-[color:var(--ink)] opacity-15"
            style={{ left: (i + 1) * cellW }}
          />
        ))}
        {Array.from({ length: ROWS - 1 }).map((_, i) => (
          <div
            key={`r${i}`}
            className="absolute left-0 right-0 border-t border-[color:var(--ink)] opacity-15"
            style={{ top: (i + 1) * cellH }}
          />
        ))}
        {/* Mice */}
        {mice.map((m) => {
          const col = m.cell % COLS;
          const row = Math.floor(m.cell / COLS);
          const cx = col * cellW + cellW / 2;
          const cy = row * cellH + cellH / 2;
          const age = (performance.now() - m.tSpawn) / 1200;
          return (
            <button
              key={m.id}
              onClick={() => hitMouse(m)}
              data-testid="mouse-target"
              className="absolute rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center text-2xl transition-transform hover:scale-110 shadow-[3px_3px_0_0_var(--ink)]"
              style={{
                left: cx - 30,
                top: cy - 30,
                width: 60,
                height: 60,
                background: "var(--paper)",
                opacity: 1 - age * 0.4,
              }}
            >
              🐭
            </button>
          );
        })}
        {state !== "playing" && (
          <div className="absolute inset-0 flex items-center justify-center flex-col bg-[rgba(255,241,230,0.9)]">
            <div className="display-serif italic text-4xl">
              {state === "idle" ? tr("Farelere dokun!") : `${tr("Süre bitti")} — ${score}`}
            </div>
            <div className="mt-2 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("Her fareye kaybolmadan tıkla · 15 saniyelik tur")}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                className="btn-pill warm"
                onClick={start}
                data-testid="mice-start"
              >
                {state === "idle" ? tr("Başla") : tr("Tekrar")} →
              </button>
              <button
                className="btn-pill ghost"
                onClick={onExit}
                data-testid="mice-exit"
              >
                {tr("Çık")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
